/* eslint-disable local-rules/no-exported-domain-types-outside-domain */

/**
 * Tweaks v2 beacon — the BroadcastChannel protocol between rendered screens
 * (the invisible screen-side beacon inside `TweaksProvider`) and the
 * /dev/tweaks page (Stage 2). Screens carry NO panel UI in v2; this channel
 * is their entire presence.
 *
 * Channel name: `cabal-tweaks2` (same-origin browser tabs only).
 *
 * ## Message shapes
 *
 * All messages are JSON-structured-clone-safe objects with a `type`
 * discriminant (`TweaksBeaconMessageType`) and an `at` epoch-ms stamp.
 *
 * ### `presence` — provider → everyone
 * ```
 * {
 *   type: "presence",
 *   tabId: string,          // stable per browser tab for the provider's life
 *   focused: boolean,       // document.hasFocus() && !document.hidden
 *   contexts: [             // what this tab currently renders
 *     {
 *       context: string,
 *       marks: [
 *         {
 *           mark: string,
 *           // Registration-carried reveal metadata (census F08): where the
 *           // mark's options can be SEEN. `available` = a live onReveal
 *           // callback exists in this tab. Null = no reveal was registered.
 *           reveal: { label: string, hint: string | null,
 *                     available: boolean } | null,
 *         },
 *       ],
 *     },
 *   ],
 *   at: number,
 * }
 * ```
 * Broadcast when: registrations mount/unmount/change, reveal metadata
 * changes, window focus/blur, visibility change, a `presence-request`
 * arrives, and on pagehide (then with `contexts: []`, which is the departure
 * signal — a consumer drops the tab). The /dev/tweaks page derives its
 * live-render indication (which tabs/marks are on the FOCUSED browser tab)
 * from `focused` + `contexts`, and its reveal affordance truthfulness from
 * the per-mark `reveal` descriptors.
 *
 * ### `presence-request` — page → all providers
 * ```
 * { type: "presence-request", at: number }
 * ```
 * Every mounted provider answers with a `presence` message. Send it once on
 * page mount to build the initial tab roster.
 *
 * ### `locate` — page → providers
 * ```
 * {
 *   type: "locate",
 *   context: string,
 *   mark: string,
 *   tabId?: string,   // omit = every tab rendering the mark reacts
 *   at: number,
 * }
 * ```
 * A provider rendering `<context>/<mark>` (and matching `tabId` when given)
 * first runs the mark's registered `onReveal` callback when one exists
 * (putting the page into the state where the options differ), then scrolls
 * the mark's registered element into view and visually flashes it. Tabs not
 * rendering the mark ignore the message.
 *
 * ### `context-updated` — any writer → everyone
 * ```
 * { type: "context-updated", context: string, at: number }
 * ```
 * The named context's JSON changed on disk (a page write, a provider
 * registration sync). Consumers re-read that context through the store seam.
 * Note: file edits made OUTSIDE the browser (agents, the CLI) cannot
 * broadcast; providers additionally re-read all registered contexts on window
 * focus to cover that path.
 */

export const TWEAKS_BEACON_CHANNEL_NAME = "cabal-tweaks2";

export enum TweaksBeaconMessageType {
  Presence = "presence",
  PresenceRequest = "presence-request",
  Locate = "locate",
  ContextUpdated = "context-updated",
}

/** Reveal metadata for one presence mark (census F08) — registration-only
 *  data mirrored onto the wire so the /dev/tweaks page can name the reveal
 *  destination and honestly disable an unavailable one. */
export interface TweaksBeaconRevealDescriptor {
  label: string;
  hint: string | null;
  /** True when a live `onReveal` callback exists in the broadcasting tab. */
  available: boolean;
}

export interface TweaksBeaconPresenceMark {
  mark: string;
  reveal: TweaksBeaconRevealDescriptor | null;
}

export interface TweaksBeaconPresenceContext {
  context: string;
  marks: TweaksBeaconPresenceMark[];
}

export interface TweaksBeaconPresenceMessage {
  type: TweaksBeaconMessageType.Presence;
  tabId: string;
  focused: boolean;
  contexts: TweaksBeaconPresenceContext[];
  at: number;
}

export interface TweaksBeaconPresenceRequestMessage {
  type: TweaksBeaconMessageType.PresenceRequest;
  at: number;
}

export interface TweaksBeaconLocateMessage {
  type: TweaksBeaconMessageType.Locate;
  context: string;
  mark: string;
  tabId?: string;
  at: number;
}

export interface TweaksBeaconContextUpdatedMessage {
  type: TweaksBeaconMessageType.ContextUpdated;
  context: string;
  at: number;
}

export type TweaksBeaconMessage =
  | TweaksBeaconPresenceMessage
  | TweaksBeaconPresenceRequestMessage
  | TweaksBeaconLocateMessage
  | TweaksBeaconContextUpdatedMessage;

export type TweaksBeaconHandler = (message: TweaksBeaconMessage) => void;

function isTweaksBeaconMessage(value: unknown): value is TweaksBeaconMessage {
  if (!value || typeof value !== "object") {
    return false;
  }
  const type = (value as { type?: unknown }).type;
  return (
    typeof type === "string" &&
    (Object.values(TweaksBeaconMessageType) as string[]).includes(type)
  );
}

/**
 * One endpoint on the beacon channel. The provider owns one per tab; the
 * /dev/tweaks page owns its own. SSR-safe: without `BroadcastChannel` (server
 * render) every method is a no-op.
 */
export class TweaksBeacon {
  private channel: BroadcastChannel | null;
  private readonly handlers = new Set<TweaksBeaconHandler>();

  constructor() {
    this.channel =
      typeof window === "undefined" || typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel(TWEAKS_BEACON_CHANNEL_NAME);
    this.channel?.addEventListener("message", (event: MessageEvent) => {
      if (!isTweaksBeaconMessage(event.data)) {
        return;
      }
      this.handlers.forEach((handler) => handler(event.data));
    });
  }

  publish(message: TweaksBeaconMessage): void {
    this.channel?.postMessage(message);
  }

  /** Returns the unsubscribe function. */
  subscribe(handler: TweaksBeaconHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  close(): void {
    this.handlers.clear();
    this.channel?.close();
    this.channel = null;
  }
}
